import { defineComponent as _defineComponent } from 'vue'
import * as yup from 'yup';
import { computed, ref, toRefs, useContext } from "@nuxtjs/composition-api";
import { useMobile } from "~/core/composables/useMobile";
import ApiService from "~/core/services/api-interaction/ApiService";

interface Props {
    sponsorUser?: object;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SignUpEnterEmail',
  props: {
    sponsorUser: { default: null }
  },
  emits: ['onContinue'],
  setup(__props: any, { emit: $emit }) {

const props = __props as { sponsorUser: object };





const { sponsorUser } = toRefs(props);
const context = useContext();
const isMobile = useMobile();

const formData = ref({
    username: '',
    email: '',
    confirmEmail: '',
})
const isSubmitted = ref(false);
const isCheckingCredentials = ref(false);
const isUsernameTaken = ref(false);
const isEmailTaken = ref(false);
let timeoutId: ReturnType<typeof setTimeout> | null = null;

const schema = yup.object().shape({
    username: yup
        .string()
        .max(255, 'username_cannot_exceed_255_characters')
        .matches(
            /^(?![-._])[a-z][-._a-z0-9]{7,}$/,
            'username_must_be_8_chars_start_with_letter_use_valid_chars'
        )
        .required('please_enter_a_valid_username'),
    email: yup
        .string()
        .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        'please_enter_a_valid_email_address'
        )
        .required('please_enter_your_email_address'),
    confirmEmail: yup
        .string()
        .oneOf([yup.ref('email'), null], 'email_addresses_do_not_match')
        .required('please_confirm_your_email_address'),
});

const errors = ref<{ [key: string]: string | null }>({ email: null, confirmEmail: null, username: null });

const usernameErrorMessage = computed(() => {
    return errors.value.username ? context.i18n.t(errors.value.username) : (formData.value.username && isUsernameTaken.value) ?
        context.i18n.t('username_taken_choose_different_username') : '';
})

const emailErrorMessage = computed(() => {
    return errors.value.email ? context.i18n.t(errors.value.email) : (formData.value.email && isEmailTaken.value) ?
        context.i18n.t('email_taken_choose_different_email') : '';
})

const validateField = async (field: string) => {
    try {
        await schema.validateAt(field, {
            username: formData.value.username,
            email: formData.value.email,
            confirmEmail: formData.value.confirmEmail
        });
        errors.value[field] = null;

        if (field === 'username') {
            isUsernameTaken.value = await validateCredentialExists({ username: formData.value.username });
        }

        if (field === 'email') {
            isEmailTaken.value = await validateCredentialExists({ email: formData.value.email });
        }
    } catch (error) {
        errors.value[field] = error.message;
    }
};

const validateCredentialExists = async (params, delay = 500) => {
    if (timeoutId) {
        clearTimeout(timeoutId);
    }

    return new Promise<boolean>((resolve) => {
        isCheckingCredentials.value = true;
        timeoutId = setTimeout(async () => {
            try {
                const response = await ApiService.query('user/check-user-exists', { params });

                resolve(response?.status === 200 ? response.data.exists : false);
            } catch (error) {
                resolve(false);
            } finally {
                isCheckingCredentials.value = false;
            }
        }, delay);
    });
};

const handleInputType = (field: string) => {
    validateField(field);

    if (field === 'email' && formData.value.confirmEmail) {
        validateField('confirmEmail');
    }

    if (field === 'confirmEmail' && formData.value.email) {
        validateField('email');
    }
}

const submitForm = () => {
    isSubmitted.value = true;

    schema.validate(
        {
            username: formData.value.username,
            email: formData.value.email,
            confirmEmail: formData.value.confirmEmail
        },
        { abortEarly: false }
    ).then(() => {
        if (isCheckingCredentials.value || isUsernameTaken.value || isEmailTaken.value) return;
        errors.value = { username: '', email: '', confirmEmail: '' };

        $emit('onContinue', { email: formData.value.email, username: formData.value.username });
    }).catch(error => {
        if (error.inner) {
            error.inner.forEach((err: yup.ValidationError) => {
                errors.value[err.path] = err.message;
            });
        }
    })
}

return { __sfc: true,props, $emit, sponsorUser, context, isMobile, formData, isSubmitted, isCheckingCredentials, isUsernameTaken, isEmailTaken, timeoutId, schema, errors, usernameErrorMessage, emailErrorMessage, validateField, validateCredentialExists, handleInputType, submitForm }
}

})